<script setup lang="ts">
import { computed } from 'vue'
import type { Tone } from '../tokens'
import type { StatTone } from '../types'
import type { ScopeNote } from '../types'
import { toneVars } from '../tone-vars'
import ScopeNotes from './ScopeNotes.vue'

/**
 * 数字卡。
 *
 * 数字卡的谎是最好撒的:把「本页取回的 50 条里有多少条」写成「总共多少条」,
 * 页面上完全看不出来,而演示时说出口就是一个错数字。所以规格 §4.3 写死了一条:
 * 数值口径要么是**全部**,要么 label/hint 里必须出现「本页」。
 * 这个组件没法替调用方判断口径,但它把 hint 摆在数值正下方 —— 想写口径就有地方写。
 *
 * 左上角那条 3px 色条是唯一表示语义色的地方:数字本身保持墨色,
 * 因为一串彩色的数字排在一起反而看不清哪个更重要。
 *
 * ═══ 明暗档为什么是两个 prop(S-42 加)═══
 *
 * 这个组件比别的组件早一轮:`tone` 在这里是**语义色**(brand / warning 那五档),
 * 明暗档走的是 `dark` 布尔,而 AppShell / PanelCard / FieldGrid 那一族用的是
 * `tone: Tone`。第三档('ops-dark',运营端深色)出现之后布尔不够用了 ——
 * 运营端深色与监管端深色**内容区同色、但数值不加柔光**(O1 画板那一排统计卡上
 * 没有大屏那种辉光),合成一个 true 会把它一起加上。
 *
 * 于是:`theme` 是新的明暗档入口(与其它组件的 tone 同一套取值),
 * `dark` 原样保留给监管端大屏那三张卡(旧签名,true 等价于 theme="dark")。
 * 两个同时给时以 `theme` 为准 —— 新代码只用 `theme`,`dark` 留到 S-48 打磨那轮再收。
 */
const props = withDefaults(
  defineProps<{
    label: string
    value: string | number
    unit?: string
    /** 口径提示,渲染在数值下方的小字 */
    hint?: string
    tone?: StatTone
    /** 明暗档。'dark' 是监管端大屏的深色(数值带柔光);'ops-dark' 是运营端深色(S-42) */
    theme?: Tone
    /** 旧签名(监管端大屏在用):true 等价于 theme="dark"。新代码用 theme */
    dark?: boolean
    /**
     * 色条位置。'left' 是这一层的旧样子(卡左上角那一条);
     * 'right' 是画板 O1 的统计卡 —— 色条在标签行右端,3×16。
     */
    bar?: 'left' | 'right'
    /** 右下角那枚航迹弧水印(深色大屏的装饰)。画板 O1 的统计卡上没有它 */
    watermark?: boolean
    notes?: readonly ScopeNote[]
  }>(),
  { tone: 'default', dark: false, bar: 'left', watermark: true },
)

/** 明暗档:新入口优先,旧的 dark 布尔是它的等价写法(true = 'dark') */
const mode = computed<Tone>(() => props.theme ?? (props.dark ? 'dark' : 'light'))
/** 只有监管端大屏那一档加柔光 —— 运营端深色(O1)的数值是平的 */
const glow = computed(() => mode.value === 'dark')

const vars = computed(() => {
  const bar = {
    default: 'var(--sd-text3)',
    brand: 'var(--sd-brand)',
    success: 'var(--sd-success)',
    warning: 'var(--sd-warning)',
    danger: 'var(--sd-danger)',
  }[props.tone]
  return { ...toneVars(mode.value), '--sd-tile-color': bar }
})

const hasNotes = computed(() => (props.notes?.length ?? 0) > 0)
</script>

<template>
  <div class="tile" :class="{ dark: glow }" :style="vars">
    <span v-if="props.bar === 'left'" class="bar" aria-hidden="true" />

    <div class="head">
      <span class="label">{{ props.label }}</span>
      <ScopeNotes v-if="hasNotes && props.notes" :notes="props.notes" :tone="mode" />
      <span v-if="props.bar === 'right'" class="bar-side" aria-hidden="true" />
    </div>

    <div class="value-row">
      <span class="value">{{ props.value }}</span>
      <span v-if="props.unit" class="unit">{{ props.unit }}</span>
    </div>

    <p v-if="props.hint" class="hint">{{ props.hint }}</p>

    <!-- 右下角的水印:一条航迹弧,几乎看不见,但让深色大屏不至于空得发慌 -->
    <svg
      v-if="props.watermark"
      class="watermark"
      width="96"
      height="96"
      viewBox="0 0 96 96"
      fill="none"
      stroke="currentColor"
      stroke-width="1.2"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="76" cy="76" r="46" />
      <circle cx="76" cy="76" r="30" />
      <path d="M24 76h72" />
    </svg>
  </div>
</template>

<style scoped>
.tile {
  position: relative;
  overflow: hidden;
  padding: 16px 18px 18px;
  border-radius: var(--sd-radius-lg);
  background: var(--sd-surface);
  border: 1px solid var(--sd-border);
  box-shadow: var(--sd-shadow-sm);
}

.bar {
  position: absolute;
  top: 16px;
  left: 0;
  width: 3px;
  height: 22px;
  border-radius: 0 var(--sd-radius-pill) var(--sd-radius-pill) 0;
  background: var(--sd-tile-color);
}

/* 标签行右端那条 3×16 的色条(画板 O1:bar="right")——
   它把原来的「卡左上角一条」换成「标签行收口处一颗」,高度也跟着量到的值走 */
.bar-side {
  margin-left: auto;
  flex: none;
  width: 3px;
  height: 16px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-tile-color);
}

.head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.label {
  font-size: 12.5px;
  color: var(--sd-text2);
}

.value-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.value {
  font-size: 32px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--sd-text1);
  font-variant-numeric: tabular-nums;
}

.unit {
  font-size: 13px;
  color: var(--sd-text3);
}

.hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--sd-text3);
  line-height: 1.6;
}

/* 深色端的数字带一层柔光 —— 「大屏感」几乎全在这一个属性上 */
.dark .value {
  text-shadow: 0 0 18px var(--sd-tile-color);
}

.watermark {
  position: absolute;
  right: -18px;
  bottom: -22px;
  color: var(--sd-tile-color);
  opacity: 0.07;
  pointer-events: none;
}
</style>
